import TagManager from 'react-gtm-module';
import React, { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import AuthContext from './AuthContext';
import api from 'services/api';
import { logger, ErrorType } from '../utils/logger';

const TrackingContext = React.createContext<{
  actions: {
    trackSignIn: (accountId?: string) => void;
    trackSignUpStart: () => void;
    trackSignUpComplete: (accountId: string) => void;
    trackFailedSignUp: (error: string) => void;
    trackAccountDeletion: (accountId: string) => void;
    sendGA4ClientId: (authToken: string) => Promise<boolean>;
    getGA4ClientId: () => string | null;
    getGA4ClientIdAsync: () => Promise<string | null>;
  };
}>({
  actions: {
    trackSignIn: (accountId?: string) => {},
    trackSignUpStart: () => {},
    trackSignUpComplete: () => {},
    trackFailedSignUp: () => {},
    trackAccountDeletion: () => {},
    sendGA4ClientId: async () => false,
    getGA4ClientId: () => null,
    getGA4ClientIdAsync: async () => null,
  },
});

interface TrackingProviderProps {
  children: React.ReactNode;
}

export function TrackingProvider(
  props: TrackingProviderProps
): React.ReactElement {
  const { children } = props;
  const location = useLocation();
  const { data: { account } } = useContext(AuthContext);

  const environment = process.env.REACT_APP_ENVIRONMENT;
  const accountId = account?.id?.toString() || '';

  useEffect(() => {
    const gtmId = process.env.REACT_APP_GTM_ID;

    if (gtmId) {
      const tagManagerArgs = {
        gtmId: gtmId,
        dataLayer: { 
          environment,
          accountId: accountId || undefined 
        },
      };
      TagManager.initialize(tagManagerArgs);
    }
  }, [environment, accountId]);

  // Push event to dataLayer
  const pushToDataLayer = (eventData: {
    event: string;
    eventCategory: string;
    eventAction: string;
    eventLabel?: string;
    accountId?: string;
    [key: string]: any;
  }) => {
    const finalAccountId = eventData.accountId || accountId || undefined;

    // Send to GTM dataLayer
    TagManager.dataLayer({
      dataLayer: {
        ...eventData,
        environment,
        accountId: finalAccountId,
      },
    });
  };

  useEffect(() => {
    // Track page views via dataLayer
    TagManager.dataLayer({
      dataLayer: {
        event: 'page_view',
        page: location.pathname + location.search,
        environment,
        accountId: accountId || undefined
      },
    });
  }, [location, accountId, environment]);

  /**
   * Extract GA4 client ID from gtag (async version)
   * 
   * Integrates with Google Analytics 4 to retrieve the client ID for marketing attribution.
   * This is critical for connecting user behavior across sessions and devices, enabling
   * proper conversion tracking and marketing campaign attribution.
   * 
   * The client ID is used to:
   * - Link user actions to marketing campaigns
   * - Enable cross-device tracking for conversion attribution  
   * - Provide data for marketing optimization and ROI analysis
   * - Support remarketing and audience building
   * 
   * @returns {Promise<string|null>} GA4 client ID or null if not found
   * 
   * Integration Notes:
   * - Requires Google Analytics to be properly initialized
   * - Fallback to synchronous method if async fails
   * - Client ID format: "123456789.1234567890"
   * - Critical for B2B SaaS marketing attribution
   */
  const extractGA4ClientIdAsync = async (): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        if (typeof window !== 'undefined' && typeof (window as any).gtag !== 'undefined') {
          const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
          if (measurementId) {
            (window as any).gtag('get', measurementId, 'client_id', (clientId: string) => {
              resolve(clientId || null);
            });
            // Timeout after 2 seconds
            setTimeout(() => resolve(null), 2000);
            return;
          }
        }
        resolve(null);
      } catch (error) {
        logger.warn('GA4 Integration: Error getting client ID from gtag (async)', error, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'extractGA4ClientIdAsync'
      });
        resolve(null);
      }
    });
  };

  /**
   * 🔧 TECHNICAL DEBT: Google Analytics Integration Complexity
   * 
   * Multiple fallback methods exist for GA4 client ID extraction due to
   * inconsistent GA4 API behavior and various deployment environments.
   * 
   * INTEGRATION CHALLENGES:
   * - GA4 gtag API has timing issues (async initialization)
   * - Different behavior in development vs production builds
   * - Cookie consent affecting GA4 initialization timing
   * - Various Google Analytics deployment methods (GTM, direct script, etc.)
   * 
   * CURRENT WORKAROUND STACK:
   * 1. Async gtag method (preferred but unreliable)
   * 2. Legacy Google Analytics object fallback
   * 3. Manual cookie parsing as last resort
   * 4. Timeout-based fallback chaining
   * 
   * MAINTENANCE ISSUES:
   * - Multiple console.warn statements for debugging various failure modes
   * - Complex promise-based timeout handling
   * - Hardcoded measurement ID dependencies
   * - No proper error categorization for different failure types
   * 
   * REFACTORING NEEDED:
   * 1. Consolidate into single, robust GA4 client ID extraction method
   * 2. Add proper retry logic with exponential backoff
   * 3. Implement GA4 readiness detection before extraction attempts
   * 4. Remove console logging in favor of proper error monitoring
   * 5. Add comprehensive unit tests for various GA4 states
   * 
   * TIMELINE: Refactor during marketing attribution system overhaul
   */
  /**
   * Extract GA4 client ID (synchronous fallback)
   * @returns {string|null} GA4 client ID in format "123456789.1234567890" or null if not found
   */
  const getGA4ClientId = (): string | null => {
    try {
      if (typeof window !== 'undefined') {
        // Method 2: Try to use Google Analytics object directly (legacy fallback)
        if (typeof window !== 'undefined' && (window as any).ga && (window as any).ga.getAll) {
          const trackers = (window as any).ga.getAll();
          if (trackers && trackers.length > 0) {
            return trackers[0].get('clientId');
          }
        }
      }

      return null;
    } catch (error) {
      logger.warn('GA4 Integration: Error extracting client ID', error, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'getGA4ClientId'
      });
      return null;
    }
  };

  /**
   * Send GA4 client ID to backend
   * @param {string} authToken - JWT auth token
   * @returns {Promise<boolean>} Success status
   */
  const sendGA4ClientId = async (authToken: string): Promise<boolean> => {
    try {
      if (!authToken) {
        logger.warn('GA4 Integration: No auth token provided', undefined, {
          errorType: ErrorType.AUTH_ERROR,
          component: 'sendGA4ClientId'
        });
        return false;
      }

      // Try async method first (preferred for GA4)
      let clientId = await extractGA4ClientIdAsync();
      
      // Fall back to synchronous method
      if (!clientId) {
        clientId = getGA4ClientId();
      }
      
      if (!clientId) {
        logger.warn('GA4 Integration: No client ID available', undefined, {
          errorType: ErrorType.NETWORK_ERROR,
          component: 'sendGA4ClientId'
        });
        return false;
      }

      const response = await api.put(
        '/account/user/settings/ga4_client_id',
        { ga4_client_id: clientId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 200) {
        logger.info('GA4 Integration: Client ID sent successfully');
        return true;
      } else {
        logger.error('GA4 Integration: Failed to send client ID', undefined, {
          errorType: ErrorType.API_ERROR,
          component: 'sendGA4ClientId',
          metadata: { statusCode: response.status }
        });
        return false;
      }
    } catch (error) {
      logger.error('GA4 Integration: Error sending client ID', error as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'sendGA4ClientId'
      });
      return false;
    }
  };

  const trackSignIn = (dynamicAccountId?: string) => {
    pushToDataLayer({
      event: 'sign_in',
      eventCategory: 'Authentication',
      eventAction: 'Sign In',
      ...(dynamicAccountId && { accountId: dynamicAccountId })
    });
  };

  const trackSignUpStart = () => {
    pushToDataLayer({
      event: 'sign_up_started',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Started',
    });
  };

  const trackSignUpComplete = (accountId: string) => {
    pushToDataLayer({
      event: 'sign_up_completed',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Completed',
      eventLabel: `Account ID: ${accountId}`,
      accountId: accountId,
    });
  };

  const trackFailedSignUp = (error: string) => {
    pushToDataLayer({
      event: 'sign_up_failed',
      eventCategory: 'Authentication',
      eventAction: 'Sign Up Failed',
      eventLabel: error,
      errorMessage: error,
    });
  };

  const trackAccountDeletion = (accountId: string) => {
    pushToDataLayer({
      event: 'account_deletion',
      eventCategory: 'Account',
      eventAction: 'Deletion',
      eventLabel: `Account ID: ${accountId}`,
      accountId: accountId,
    });
  };

  return (
    <TrackingContext.Provider
      value={{
        actions: {
          trackSignIn,
          trackSignUpStart,
          trackSignUpComplete,
          trackFailedSignUp,
          trackAccountDeletion,
          sendGA4ClientId,
          getGA4ClientId,
          getGA4ClientIdAsync: extractGA4ClientIdAsync,
        },
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
}
export default TrackingContext;
